import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { Sparkle, Trash, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";

function parseJson(raw) {
  if (!raw) return null;
  try { return JSON.parse(raw); } catch {}
  const m = raw.match(/\{[\s\S]*\}/);
  if (m) { try { return JSON.parse(m[0]); } catch {} }
  return null;
}

export default function LandingPages() {
  const { activeBiz } = useOutletContext();
  const [pages, setPages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [city, setCity] = useState("");
  const [open, setOpen] = useState(null);

  const reload = async () => {
    if (!activeBiz) return;
    try {
      const { data } = await api.get(`/businesses/${activeBiz.id}/landing-pages`);
      setPages(data);
    } catch {}
  };
  useEffect(() => {
    reload();
    if (activeBiz) {
      setKeyword((activeBiz.keywords || [])[0] || "");
      setCity(activeBiz.city || "");
    }
    // eslint-disable-next-line
  }, [activeBiz]);

  if (!activeBiz) return <div className="p-12 text-zinc-500">Add a business first.</div>;

  const generate = async () => {
    if (!keyword || !city) return toast.error("Pick a keyword and city");
    setBusy(true);
    try {
      await api.post(`/businesses/${activeBiz.id}/landing-pages`, { keyword, city });
      toast.success("Landing page generated");
      reload();
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };

  const remove = async (id) => {
    try { await api.delete(`/businesses/${activeBiz.id}/landing-pages/${id}`); reload(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="p-6 lg:p-10 space-y-6 max-w-6xl">
      <header>
        <div className="label-tiny mb-1">DFY Ranking</div>
        <h1 className="font-display text-4xl font-black tracking-tighter" data-testid="lp-title">AI Local Landing Pages</h1>
        <p className="text-zinc-500 mt-1">Auto-generate SEO-optimized city × service pages that rank in Google AND get cited by AI search.</p>
      </header>

      <div className="bento p-5 grid grid-cols-1 md:grid-cols-3 gap-3 items-end" data-testid="lp-form">
        <div>
          <label className="label-tiny block mb-2">Service / keyword</label>
          <select className="input-field" value={keyword} onChange={(e) => setKeyword(e.target.value)} data-testid="lp-keyword">
            <option value="">— pick —</option>
            {(activeBiz.keywords || []).map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </div>
        <div>
          <label className="label-tiny block mb-2">Target city</label>
          <input className="input-field" value={city} onChange={(e) => setCity(e.target.value)} data-testid="lp-city" />
        </div>
        <button className="btn-primary justify-center" onClick={generate} disabled={busy} data-testid="lp-generate">
          {busy ? <span className="spinner"></span> : <><Sparkle size={14} weight="fill" /> Generate</>}
        </button>
      </div>

      <div className="space-y-3">
        {pages.length === 0 && <div className="bento p-8 text-zinc-500 text-center">No pages yet. Generate your first.</div>}
        {pages.map((p) => {
          const j = parseJson(p.raw);
          return (
            <div key={p.id} className="bento" data-testid={`lp-row-${p.id}`}>
              <div className="p-5 flex items-start justify-between gap-4">
                <div>
                  <div className="font-display font-bold text-lg">{j?.h1 || `${p.keyword} in ${p.city}`}</div>
                  <div className="font-mono text-xs text-zinc-500 mt-1">/{p.slug}</div>
                  <div className="font-mono text-xs text-zinc-600 mt-1">{j?.meta_description || j?.meta_title || "—"}</div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button className="btn-secondary !py-2 !text-[11px]" onClick={() => setOpen(open === p.id ? null : p.id)} data-testid={`lp-toggle-${p.id}`}><Eye size={12} /> {open === p.id ? "Hide" : "View"}</button>
                  <button className="btn-secondary !py-2 !text-[11px]" onClick={() => remove(p.id)} data-testid={`lp-del-${p.id}`}><Trash size={12} /></button>
                </div>
              </div>
              {open === p.id && j && (
                <div className="border-t border-zinc-200 p-5 space-y-3 bg-zinc-50 text-sm">
                  <div><div className="label-tiny">Meta title</div><div className="font-mono">{j.meta_title}</div></div>
                  <div><div className="label-tiny">Intro</div><p className="leading-relaxed whitespace-pre-wrap">{j.intro}</p></div>
                  {Array.isArray(j.bullets) && (
                    <div><div className="label-tiny">Differentiators</div><ul className="list-disc pl-5 space-y-1">{j.bullets.map((b, i) => <li key={i}>{b}</li>)}</ul></div>
                  )}
                  {Array.isArray(j.faqs) && (
                    <div><div className="label-tiny">FAQs</div>
                      {j.faqs.map((f, i) => (
                        <div key={i} className="mt-2"><div className="font-bold">{f.question || f.q}</div><p className="text-zinc-700">{f.answer || f.a}</p></div>
                      ))}
                    </div>
                  )}
                  {Array.isArray(j.schema_keywords) && (
                    <div><div className="label-tiny">Schema keywords</div><div className="font-mono text-xs">{j.schema_keywords.join(", ")}</div></div>
                  )}
                </div>
              )}
              {open === p.id && !j && (
                <div className="border-t border-zinc-200 p-5 bg-zinc-50">
                  <pre className="font-mono text-xs whitespace-pre-wrap">{p.raw}</pre>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
